<template>
  <div class="Step0Form">
    <template v-if="showTitle">
      <h2 class="title">{{ answers.product!.productName }}</h2>

      <h3 class="subtitle">{{ t('copy_step0_subtitle') }}</h3>
    </template>

    <DatePickerComponent
      id="start-date"
      :label="t('rater.common.modify-search-view.start-date')"
      v-model="repricingAnswers.startDate"
      :datePickerVisible="false"
      :min-date="new Date()"
      :max-date="addDaysToDate(configurations.maxDays)"
      :rules="[required]"
    />

    <BirthDatesComponent
      v-model="repricingAnswers.insureds"
      :for-whom="repricingAnswers.forWhom!"
      :configurations="configurations"
      :start-date="repricingAnswers.startDate"
      :use-birth-date-actuarial="true"
      :rules="[required]"
    />

    <template v-if="mandatoryCoverageSlider.min && mandatoryCoverageSlider.max">
      <FormField
        class="mb-5"
        :statement="
          t('copy_life_modify-insurance_coverages-title', {
            min: addCommas(mandatoryCoverageSlider.min),
            max: addCommas(mandatoryCoverageSlider.max)
          })
        "
      >
        <AdvantageSliderComponent
          id="coverage-slider"
          v-model="coveragesCapital"
          :min="mandatoryCoverageSlider.min"
          :max="mandatoryCoverageSlider.max"
          :step="mandatoryCoverageSlider.step"
          :footer-message="
            t(`copy_common_slider-footer_min-max`, {
              min: addCommas(mandatoryCoverageSlider.min),
              max: addCommas(mandatoryCoverageSlider.max)
            })
          "
        />
      </FormField>

      <FormField
        v-if="showRevaluation"
        :statement="t('copy_life_modify-insurance_reevaluation-title')"
      >
        <BaseSelect
          id="reevaluation-type"
          class="reevaluation-type"
          v-model="reevaluationType"
          :value="reevaluationType"
          :items="reevaluationOptions"
        ></BaseSelect>
      </FormField>

      <p class="mb-4 text-black">
        {{ t('copy_life_modify-insurance_coverages-statement') }}
      </p>

      <CoverageComplete
        v-for="(coverage, i) in optionalCoverages"
        class="mb-4"
        :id="coverage.coverageCode"
        :key="coverage.coverageCode"
        :text="coverage.title"
        :tooltip="coverage.tooltip"
        :capitals="getSliderCapitals(coverage)"
        v-model:checked="optionalToSave[i].checked"
        v-model:capital="optionalToSave[i].value"
      />
    </template>

    <BaseSelect
      :label="t('rater.common.modify-search-view.payment')"
      id="payment-frequency"
      :items="paymentPeriodChoices"
      v-model="repricingAnswers.billingCycle"
    />

    <template v-if="askForProfessionAndActivity">
      <BaseAutocompleteInputStrict
        id="address"
        icon="icon-maletin-comercial"
        :label="t('copy_life_professions-and-activities-form_professions-question')"
        v-model="profession"
        :items="professions.map(({ label }) => label)"
        :placeholder="t('common.select.placeholder')"
        :rules="[required]"
      />

      <CardInfoComponent
        v-if="isProfessionLowRisk"
        :icon="configurations.tags.professionLowRiskAlert.icon"
      >
        {{ configurations.tags.professionLowRiskAlert.label }}
      </CardInfoComponent>

      <CardInfoComponent
        v-else-if="isProfessionHighRisk"
        :icon="configurations.tags.professionHighRiskAlert.icon"
      >
        {{ configurations.tags.professionHighRiskAlert.label }}
      </CardInfoComponent>

      <FormField :statement="t('copy_life_professions-and-activities-form_activities-question')">
        <BaseSelect
          id="payment-frequency"
          :items="activities"
          v-model="activityCode"
        />
      </FormField>

      <CardInfoComponent
        v-if="isActivityHighRisk"
        :icon="configurations.tags.activityHighRiskAlert.icon"
      >
        {{ configurations.tags.activityHighRiskAlert.label }}
      </CardInfoComponent>
    </template>

    <div class="actions">
      <BaseButton
        @click="checkRisk"
        :ariaDisabled="!isValidForm || !changeData || riskChecked !== RiskCheckedStateEnum.NONE"
      >
        {{ t('rater.base.submit') }}
      </BaseButton>

      <template v-if="showActionsAfterRiskCheck">
        <v-divider class="divider-blue" />

        <BaseButton
          v-if="riskChecked === RiskCheckedStateEnum.DISALLOW"
          @click="openPhoneModal"
        >
          {{ t('copy_button_continue-by-phone') }}
        </BaseButton>

        <!-- cambiar a "amount=price" si vuelve a haber descuentos -->
        <template v-else-if="askToCalculateInsurance && price">
          <TotalAmountComponent
            :label="t('hiring.common.step-0.price-updated-label')"
            :amount="priceWithoutDiscount"
            :currency="paymentUnits"
            :period="`/${period}`"
          />

          <BaseButton @click="repricing">
            {{ t('hiring.common.step-0.price-updated-button') }}
          </BaseButton>
        </template>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { useRepricing } from '@/modules/common/hiring/composables/useRepricing'
  import { useErrorManagerComposable } from '@/modules/core/composables/errorManagerComposable'
  import { useRules } from '@/modules/core/composables/useRulesComposable'
  import { useLoadingStore } from '@/modules/core/stores/loadingStore'
  import { useSupportModalStore } from '@/modules/core/stores/supportModalStore'
  import { useLifeStore } from '@/modules/life/hiring/stores/index'
  import { addDaysToDate } from '@/utils/date.utils'
  import { computed, onBeforeMount, ref, watch, type Ref } from 'vue'
  import { useI18n } from 'vue-i18n'

  import CoverageComplete from '@/modules/common/common/components/coverages/CoverageComplete.vue'
  import BirthDatesComponent from '@/modules/common/common/components/panels/BirthDatesComponent.vue'
  import CardInfoComponent from '@/modules/common/common/components/panels/CardInfoComponent.vue'
  import type AdditionalCoverage from '@/modules/common/common/models/APP/AdditionalCoverage'
  import TotalAmountComponent from '@/modules/common/hiring/components/TotalAmountComponent.vue'
  import BaseAutocompleteInputStrict from '@/modules/core/components/ui/BaseAutocompleteInputStrict.vue'
  import BaseButton from '@/modules/core/components/ui/BaseButton.vue'
  import BaseSelect from '@/modules/core/components/ui/BaseSelect.vue'
  import DatePickerComponent from '@/modules/core/components/ui/DatePickerComponent.vue'
  import AdvantageSliderComponent from '@/modules/core/components/utils/AdvantageSliderComponent.vue'
  import FormField from '@/modules/core/components/utils/FormField.vue'
  import { useCoverages } from '@/modules/life/common/composables/useCoveragesComposable'
  import { useProfessions } from '@/modules/life/common/composables/useProfessionsComposable'
  import type LifeConfigurations from '@/modules/life/common/models/APP/LifeConfigurations'
  import type LifeInsurance from '@/modules/life/common/models/APP/LifeInsurance'
  import LifeHiringAnswers from '@/modules/life/hiring/models/APP/LifeHiringAnswers'
  import { addCommas } from '@/utils/textUtils'

  const { t } = useI18n()
  const { required } = useRules()
  const lifeStore = useLifeStore()
  const loadingStore = useLoadingStore()
  const errorManager = useErrorManagerComposable()
  const supportModalStore = useSupportModalStore()

  const {
    mandatoryCoverageSlider,
    optionalCoverages,
    mandatoryToSave,
    optionalToSave,
    getSliderCapitals,
    loadCoverages
  } = useCoverages()

  const emit = defineEmits(['repricing'])

  interface PropsInterface {
    nextForm: String[]
    answers: LifeHiringAnswers
    configurations: LifeConfigurations
    isValidForm: boolean
    showTitle?: boolean
  }
  const props = withDefaults(defineProps<PropsInterface>(), {
    showTitle: true
  })

  const answers = ref(props.answers)
  const reevaluationType = ref(
    props.answers.reevaluationType?.value ?? props.configurations.reevaluationOptions[0].value
  )
  const repricingAnswers = ref<LifeHiringAnswers>(
    new LifeHiringAnswers(answers.value)
  ) as Ref<LifeHiringAnswers>

  const coveragesCapital = ref<number>(getInitCoveragesCapital())
  const showRevaluation = computed(
    () =>
      repricingAnswers.value.insuranceTypes?.includes(
        Number(props.configurations.insuranceTypes.mortgage.id)
      )
  )

  const {
    NONE_VALUE,
    RiskCheckedStateEnum,

    professionCode,
    activityCode,
    professions,
    activities,
    riskChecked,
    isProfessionHighRisk,
    isProfessionLowRisk,
    isActivityHighRisk,
    professionPriceRice,
    activityPriceRice,
    profession,
    skipRiskChecking,
    getRiskCheckedState
  } = useProfessions(props.answers)

  const {
    price,
    period,
    changeData,
    paymentUnits,
    paymentPeriodChoices,
    askToCalculateInsurance,
    initializeCommon,
    simulateRepricingCommon,
    repricingCommon
  } = useRepricing<LifeHiringAnswers, LifeInsurance>(repricingAnswers, props.configurations)

  const askForProfessionAndActivity = !!professionCode.value

  const showActionsAfterRiskCheck = computed(() => {
    if (riskChecked.value === RiskCheckedStateEnum.DISALLOW) {
      return true
    }

    return askToCalculateInsurance.value && price.value && !changeData.value
  })

  const priceWithoutDiscount = computed(() => {
    // Usa el precio directamente sin considerar el descuento
    return price.value
  })

  const reevaluationOptions = computed(() =>
    props.configurations.reevaluationOptions.map((option) => ({
      label: option.description,
      value: option.value
    }))
  )

  watch(
    () => repricingAnswers.value,
    () => {
      changeData.value = true
    },
    { deep: true }
  )

  watch(
    () => coveragesCapital.value,
    (capital: number | null) => {
      mandatoryToSave.value.forEach((c) => {
        c.value = capital
      })
      optionalToSave.value.forEach((c) => {
        const optionalCoverage = optionalCoverages.value.find((o) => o.coverageCode === c.key)

        if (!optionalCoverage?.coverageInfoCapitals.length) {
          c.value = capital
        }
      })
    }
  )

  watch(
    () => [mandatoryToSave.value, optionalToSave.value],
    () => (changeData.value = true),
    { deep: true }
  )

  watch(
    () => professionCode.value,
    (value: string) => {
      repricingAnswers.value.insureds[0].profession = value
      riskChecked.value = RiskCheckedStateEnum.NONE
    }
  )

  watch(
    () => activityCode.value,
    (value: string) => {
      repricingAnswers.value.insureds[0].activity = value
      riskChecked.value = RiskCheckedStateEnum.NONE
      isActivityHighRisk.value = false
    }
  )

  watch(
    () => reevaluationType.value,
    (value: string) => {
      repricingAnswers.value.reevaluationType =
        props.configurations.reevaluationOptions.find((o) => o.value === value) ?? null
    }
  )

  watch(
    () => repricingAnswers.value.insureds[0],
    async (insured) => {
      const storeCoverages = await lifeStore.loadCoverages()
      await loadCoverages(
        storeCoverages,
        insured,
        props.configurations.capitalOptions,
        coveragesCapital.value
      )
    },
    { deep: true }
  )

  function openPhoneModal() {
    supportModalStore.setOpenModal(true)
  }

  function getInitCoveragesCapital(additionalCoverages: AdditionalCoverage[] = []) {
    const firstCoverageCapital = additionalCoverages.length ? additionalCoverages[0].value : null

    if (firstCoverageCapital) return firstCoverageCapital

    return mandatoryCoverageSlider.value.min ?? props.configurations.capitalOptions[0].min
  }

  async function checkRisk() {
    if (skipRiskChecking()) {
      await simulateRepricing()
      return
    }

    const [onlyProfession, onlyActivity, both] = await Promise.all([
      lifeStore.getIncrementRisk(Number(professionCode.value), Number(NONE_VALUE)),
      lifeStore.getIncrementRisk(Number(NONE_VALUE), Number(activityCode.value)),
      lifeStore.getIncrementRisk(Number(professionCode.value), Number(activityCode.value))
    ])

    isProfessionLowRisk.value = onlyProfession.allowsHiring && onlyProfession.priceRice
    isProfessionHighRisk.value = !onlyProfession.allowsHiring
    professionPriceRice.value = onlyProfession.allowsHiring && onlyProfession.priceRice

    isActivityHighRisk.value = !onlyActivity.allowsHiring
    activityPriceRice.value = !!onlyActivity.priceRice
    riskChecked.value = getRiskCheckedState(both.allowsHiring, both.priceRice)

    if (riskChecked.value !== RiskCheckedStateEnum.DISALLOW) {
      await simulateRepricing()
    }
  }

  function calculateRevaluationType() {
    if (!showRevaluation.value) return null
    return (
      props.configurations.reevaluationOptions.find(
        (option) => option.value === reevaluationType.value
      ) ?? null
    )
  }

  async function simulateRepricing() {
    try {
      const coverages = mandatoryToSave.value.concat(optionalToSave.value) as AdditionalCoverage[]
      repricingAnswers.value.insureds[0].additionalCoverages = coverages
      repricingAnswers.value.reevaluationType = calculateRevaluationType()
      await simulateRepricingCommon(lifeStore.getNewRaterPrice)
    } catch (error) {
      errorManager.handleErrorInsight(error)
    }
  }

  async function repricing() {
    try {
      await repricingCommon(lifeStore.getNewRaterPrice, lifeStore.hireProduct)
      const customEvent = new CustomEvent('life-repricing-step-0', {
        detail: {
          answers: repricingAnswers.value
        }
      })

      document.dispatchEvent(customEvent)
      emit('repricing')
    } catch (error) {
      errorManager.handleErrorInsight(error)
    }
  }

  async function beforeMount() {
    try {
      loadingStore.startLoading()
      const [storeCoverages, _professions, _activities] = await Promise.all([
        lifeStore.loadCoverages(),
        lifeStore.fetchProfessions(),
        lifeStore.fetchActivities()
      ])

      const insured = props.answers.insureds[0]
      coveragesCapital.value = getInitCoveragesCapital(insured.additionalCoverages)
      await loadCoverages(
        storeCoverages,
        insured,
        props.configurations.capitalOptions,
        coveragesCapital.value
      )
      professions.value = _professions
      activities.value = _activities

      initializeCommon(props.answers)
    } catch (error) {
      errorManager.handleError(error)
    } finally {
      loadingStore.stopLoading()
    }
  }

  onBeforeMount(beforeMount)
</script>

<style lang="scss" scoped>
  .Step0Form {
    .title {
      font-size: $font-size-20;
      font-weight: $font-weight-500;
    }

    .subtitle {
      color: $color-neutral;
      font-size: $font-size-m;
      margin-bottom: $spacing-2xl;
    }
  }
</style>
